import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssetService from '../Services/assets.service';
import { buildCapitalGainResult } from '../Utils/capitalGainCalculations';
import { loadCapitalGainInputs, readableCapitalGainError, runWithConcurrency } from '../Utils/capitalGainLoader';
import {
    CAPITAL_GAIN_SUMMARY_STATUS,
    buildCapitalGainSnapshotItem,
    getSnapshotAssetId,
    summarizeCapitalGainResult,
    summarizeCapitalGainSnapshot,
} from '../Utils/capitalGainSnapshot';

const CONCURRENCY = 3;
const LOADING_SUMMARY = { status: CAPITAL_GAIN_SUMMARY_STATUS.loading };

/**
 * Capital-gain summary per sold asset: the saved snapshot when there is one, otherwise the
 * live result with default inputs. Live results are computed only for `assets` (e.g. the rows on screen).
 */
export default function useCapitalGainYearSummary({
    caseId,
    token,
    caseType,
    assets = [],
    enabled = true,
}) {
    const [snapshots, setSnapshots] = useState({});
    const [snapshotsLoaded, setSnapshotsLoaded] = useState(false);
    const [computed, setComputed] = useState({});
    const [saving, setSaving] = useState(false);
    const inFlight = useRef(new Set());
    const computedRef = useRef(computed);
    computedRef.current = computed;

    const loadSnapshots = useCallback(async () => {
        if (!caseId || !token) return;
        try {
            const resp = await AssetService.GetCapitalGainSnapshots(caseId, null, token);
            const map = {};
            (resp?.data || []).forEach((snapshot) => {
                map[getSnapshotAssetId(snapshot)] = snapshot;
            });
            setSnapshots(map);
        } catch (error) {
            console.error('Failed to load capital gain snapshots:', error);
            setSnapshots({});
        } finally {
            setSnapshotsLoaded(true);
        }
    }, [caseId, token]);

    const refresh = useCallback(
        async (assetId = null) => {
            setComputed((prev) => {
                if (!assetId) return {};
                const next = { ...prev };
                delete next[assetId];
                return next;
            });
            await loadSnapshots();
        },
        [loadSnapshots]
    );

    useEffect(() => {
        if (!enabled) return;
        setSnapshotsLoaded(false);
        refresh();
    }, [enabled, refresh]);

    const computeOne = useCallback(
        async (asset) => {
            const inputs = await loadCapitalGainInputs({
                caseId,
                assetId: asset.id,
                token,
                fallbackAsset: asset,
            });
            const result = buildCapitalGainResult({
                basis: inputs.basis,
                cpi: inputs.cpi,
                enhancements: inputs.enhancements,
                replacements: inputs.replacements,
                caseType,
            });
            if (!result) throw new Error(inputs.cpiError || 'FailedToLoadCapitalGainData');
            return result;
        },
        [caseId, token, caseType]
    );

    const computeMissing = useCallback(
        async (list) => {
            const pending = list.filter((asset) => asset?.id && !inFlight.current.has(asset.id));
            if (pending.length === 0) return;
            pending.forEach((asset) => inFlight.current.add(asset.id));
            setComputed((prev) => {
                const next = { ...prev };
                pending.forEach((asset) => {
                    next[asset.id] = LOADING_SUMMARY;
                });
                return next;
            });

            const results = await runWithConcurrency(pending, CONCURRENCY, computeOne);
            const updates = {};
            pending.forEach((asset, index) => {
                const outcome = results[index];
                updates[asset.id] =
                    outcome.status === 'fulfilled'
                        ? summarizeCapitalGainResult(outcome.value)
                        : { status: CAPITAL_GAIN_SUMMARY_STATUS.error, error: readableCapitalGainError(outcome.reason) };
                inFlight.current.delete(asset.id);
            });
            setComputed((prev) => ({ ...prev, ...updates }));
        },
        [computeOne]
    );

    const assetKey = assets.map((asset) => asset?.id).join('|');

    useEffect(() => {
        if (!enabled || !snapshotsLoaded) return;
        const missing = assets.filter(
            (asset) => asset?.id && !snapshots[asset.id] && !computed[asset.id]
        );
        if (missing.length > 0) computeMissing(missing);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, snapshotsLoaded, assetKey, snapshots, computed, computeMissing]);

    const getSummary = useCallback(
        (assetId) => {
            if (!snapshotsLoaded) return LOADING_SUMMARY;
            if (snapshots[assetId]) return summarizeCapitalGainSnapshot(snapshots[assetId]);
            return computed[assetId] || LOADING_SUMMARY;
        },
        [snapshotsLoaded, snapshots, computed]
    );

    const summaries = useMemo(() => {
        const map = {};
        assets.forEach((asset) => {
            if (asset?.id) map[asset.id] = getSummary(asset.id);
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetKey, getSummary]);

    /** Saves a snapshot for every listed asset that has none yet. */
    const saveAssets = useCallback(
        async (list) => {
            const toSave = list.filter((asset) => asset?.id && !snapshots[asset.id]);
            if (toSave.length === 0) return { saved: 0, failed: 0 };

            setSaving(true);
            try {
                const resultById = {};
                const need = [];
                toSave.forEach((asset) => {
                    const existing = computedRef.current[asset.id]?.result;
                    if (existing) resultById[asset.id] = existing;
                    else need.push(asset);
                });
                const fresh = await runWithConcurrency(need, CONCURRENCY, computeOne);
                need.forEach((asset, index) => {
                    if (fresh[index].status === 'fulfilled') resultById[asset.id] = fresh[index].value;
                });

                const items = toSave
                    .filter((asset) => resultById[asset.id])
                    .map((asset) => buildCapitalGainSnapshotItem(asset.id, resultById[asset.id]));

                if (items.length > 0) {
                    const resp = await AssetService.SaveCapitalGainSnapshots(caseId, items, token);
                    if (!resp?.success) throw new Error(resp?.error || 'FailedToSaveCapitalGainSnapshot');
                    setSnapshots((prev) => {
                        const next = { ...prev };
                        (resp.data || []).forEach((snapshot) => {
                            next[getSnapshotAssetId(snapshot)] = snapshot;
                        });
                        return next;
                    });
                }
                return { saved: items.length, failed: toSave.length - items.length };
            } finally {
                setSaving(false);
            }
        },
        [snapshots, computeOne, caseId, token]
    );

    /** Deletes the snapshots of a year (or of one asset) and recomputes them. */
    const resetAssets = useCallback(
        async ({ year, soldAssetId = null }) => {
            setSaving(true);
            try {
                const resp = await AssetService.DeleteCapitalGainSnapshots(caseId, year, token, soldAssetId);
                if (!resp?.success) throw new Error(resp?.error || 'FailedToResetCapitalGainSnapshot');
                const removed = Object.values(snapshots)
                    .filter((snapshot) =>
                        soldAssetId
                            ? getSnapshotAssetId(snapshot) === soldAssetId
                            : Number(snapshot.taxYear) === Number(year)
                    )
                    .map(getSnapshotAssetId);
                setSnapshots((prev) => {
                    const next = { ...prev };
                    removed.forEach((id) => delete next[id]);
                    return next;
                });
                setComputed((prev) => {
                    const next = { ...prev };
                    removed.forEach((id) => delete next[id]);
                    return next;
                });
                return resp.deletedCount ?? removed.length;
            } finally {
                setSaving(false);
            }
        },
        [caseId, token, snapshots]
    );

    return {
        summaries,
        snapshots,
        loading: !snapshotsLoaded,
        saving,
        getSummary,
        saveAssets,
        resetAssets,
        refresh,
        reloadSnapshots: loadSnapshots,
    };
}
